/* eslint-disable no-underscore-dangle */
import { reactive } from 'vue'
import { GtmSupport, useGtm } from '@gtm-support/vue-gtm'
import { AuthService } from './auth'
import { JBGApi } from './api'

import { useState } from '#app'

export namespace User {
    export enum Status {
        Unknown,
        Verified,
        Unverified,
        CreatingByEmail,
        CreatingBySSO
    }
}

interface CurrentUserState {
    id: number;
    dob: string;
    email: string;
    avatar: string;
    isDirty: boolean;
    provider: string;
    mixpanelId: string;
    username: string;
    _isLoggedIn: boolean;
    _status: User.Status;
}

interface UseCurrentUser {
    state: Ref<CurrentUserState>;
    setData: (user: UserApi.Response) => void;
    logout: () => void;
    isLoggedIn: () => boolean;
    getStatus: () => User.Status;
    setStatus: (status: User.Status) => void;
}

export const useCurrentUser = () => {
    const state = useState('currentUser', () => reactive<CurrentUserState>({
        id: 0,
        dob: '',
        email: '',
        isDirty: true,
        provider: '',
        mixpanelId: '',
        username: '',
        avatar: '',
        _isLoggedIn: false,
        _status: User.Status.Unknown
    }))

    const setData = (user: UserApi.Response): void => {
        state.value.id = user.id
        state.value.dob = user.dob ?? ''
        state.value.email = user.email ?? ''
        state.value.avatar = user.avatar ?? ''
        state.value.mixpanelId = user.mixpanelId ?? ''
        state.value.provider = user.provider ?? ''
        state.value.username = user.username ?? ''
        state.value._isLoggedIn = true
    }

    const getStatus = (): User.Status => state.value._status

    const setStatus = (status: User.Status) => {
        state.value._status = status
    }

    const logout = (): void => {
        state.value.id = 0
        state.value.dob = ''
        state.value.email = ''
        state.value.avatar = ''
        state.value.mixpanelId = ''
        state.value.provider = ''
        state.value.username = ''
        state.value._isLoggedIn = false
    }

    const isLoggedIn = (): boolean => state.value._isLoggedIn

    return {
        state, setData, logout, isLoggedIn, getStatus, setStatus
    }
}

export class UserApi extends JBGApi {
    private authService: AuthService
    private currentUser: UseCurrentUser
    private gtm: GtmSupport | undefined

    constructor(options: UserApi.Options) {
        super(options.binjpipeUrl, options.authService)
        this.authService = options.authService
        this.currentUser = useCurrentUser()
        this.gtm = useGtm()
    }

    get isLoggedIn(): boolean {
        const accessExpirationCookie = document.cookie.indexOf('binjpipe-access-expiration-token=') !== -1
        if (accessExpirationCookie) {
            return true
        }
        return false
    }

    // signup via email/pwd for new users
    // eslint-disable-next-line max-len
    async emailSignup(email: string, password: string, confirmPassword: string, username: string, dob: string): Promise<Response | JBGApi.Error> {
        try {
            const route = 'user/emailsignup'
            const options = this.buildOptions('POST', {
                email,
                password,
                confirmPassword,
                username,
                dob
            })

            return this.callAPI(route, options)
        } catch (error) {
            return error as JBGApi.Error
        }
    }

    public async getUserByEmail(email: string, provider: string): Promise<Response> {
        let route = `user/exists/${email}`
        if (provider) {
            route = `${route}?provider=${provider}`
        }
        const options = this.buildOptions('GET')
        return this.callAPI(route, options)
    }

    public async selfGrantStreamEntitlement() {
        const route = 'user/streamingentitlement'
        const options = this.buildOptions('POST')
        return this.callAPI(route, options)
    }

    // login via Google auth
    async login() {
        const BINJ_ACCESS_COOKIE_EXPIRATION = 60
        // save the current date/time plus the expiration of the binjpipe-access-cookie - 1m to check against later
        const currentTime = new Date()
        const expirationTimeWindow = new Date(currentTime.setMinutes(currentTime.getMinutes() + (BINJ_ACCESS_COOKIE_EXPIRATION - 1)))

        try {
            await this.authService.login(AuthService.Providers.GOOGLE)

            sessionStorage.setItem('authExpiration', expirationTimeWindow.toString())
        } catch (error) {
            console.error(error)
        } finally {
            this.currentUser.state.value.isDirty = true
        }
    }
    // login via email
    async emailLogin(email: string, password: string): Promise<Response> {
        try {
            return this.authService.emailSignin(email, password)
        } finally {
            this.currentUser.state.value.isDirty = true
        }
    }

    async logout(): Promise<void> {
        try {
            await this.authService.logout(this.currentUser.state.value.id)
            if (this.gtm) {
                this.gtm.trackEvent({
                    event: 'Mixpanel - Reset'
                })
            }
            // clear out local data and reroute to login
            this.currentUser.logout()
            const localeRoute = useLocaleRoute()
            void navigateTo(localeRoute('/signin'))
        } catch (err) {
            console.error('failed to log user out.', err)
        }
    }

    async refreshToken() {
        const res = await this.authService.refreshToken()
        if (res.status !== 204) {
            // force log out
            void this.logout()
        }
    }

    async verifyEmail(code: string, email: string): Promise<Response | JBGApi.Error> {
        try {
            const route = 'user/verify'
            const options = this.buildOptions('POST', {
                code,
                email
            })

            return this.callAPI(route, options)
        } catch (error) {
            return error as JBGApi.Error
        }
    }

    async sendVerification(email: string): Promise<Response | JBGApi.Error> {
        try {
            const route = 'user/sendverification'
            const options = this.buildOptions('POST', {
                email
            })

            return this.callAPI(route, options)
        } catch (error) {
            return error as JBGApi.Error
        }
    }

    async checkUserName(username: string): Promise<Response | JBGApi.Error> {
        try {
            const route = `user/checkusername/${username}`
            const options = this.buildOptions('GET')

            return this.callAPI(route, options)
        } catch (error) {
            return error as JBGApi.Error
        }
    }

    async updatePassword(currentPassword: string, newPassword: string) {
        try {
            const route = 'user/changepassword'
            const options = this.buildOptions('POST', {
                currentPassword,
                newPassword
            })

            return this.callAPI(route, options)
        } catch (error) {
            return error as JBGApi.Error
        }
    }

    checkLastAuthenticated(userIsAuthenticated: boolean) {
        const authExpiration = sessionStorage.getItem('authExpiration')
        if (authExpiration) {
            // if user is not authenticated, remove lastAuthentication and return
            if (!userIsAuthenticated) {
                sessionStorage.removeItem('authExpiration')
                return
            }

            const currentTime = new Date()
            const authIsExpired = Date.parse(authExpiration) < Date.parse(String(currentTime))
            if (!authIsExpired) {
                return
            }

            // if expired, attempt a refresh
            void this.refreshToken()
        }
    }

    public async update(user: UserApi.UpdateRequest): Promise<Response | JBGApi.Error> {
        const route = 'user'
        const options = this.buildOptions('POST', {
            username: user.username,
            dob: user.dob,
            avatar: user.avatar
        })
        return this.callAPI(route, options)
    }

    public async loadUserData(): Promise<void | JBGApi.Error> {
        const route = 'user'
        const options = this.buildOptions('GET')
        try {
            const res = await this.callAPI<UserApi.Response>(route, options)
            if (res.mixpanelId) {
                if (this.gtm) {
                    this.gtm.trackEvent({
                        event: 'Mixpanel - Identify User',
                        value: res.mixpanelId
                    })
                }
            }
            this.currentUser.setData(res)
        } catch (error) {
            return error as JBGApi.Error
        } finally {
            this.currentUser.state.value.isDirty = false
        }
    }

    async forgotPassword(email: string): Promise<Response | JBGApi.Error> {
        try {
            return this.authService.forgotPassword(email)
        } catch (error) {
            return error as JBGApi.Error
        }
    }

    async confirmForgotPassword(email: string, code: string, password: string): Promise<Response | JBGApi.Error> {
        try {
            return this.authService.confirmForgotPassword(email, code, password)
        } catch (error) {
            return error as JBGApi.Error
        }
    }

    async deleteUser(): Promise<Response | JBGApi.Error> {
        const route = 'user'
        const options = this.buildOptions('DELETE')
        return this.callAPI(route, options)
    }

    async getUserAuthStatus(): Promise<boolean> {
        if (this.currentUser.state.value.isDirty) {
            await this.loadUserData()
        }
        this.checkLastAuthenticated(this.currentUser.isLoggedIn())

        return this.isLoggedIn
    }

    async getEntitlements(): Promise<JBGApi.PagedResult<UserApi.UserEntitlement>> {
        const route = 'user/entitlements'
        const options = this.buildOptions('GET')
        return this.callAPI(route, options)
    }
}

export namespace UserApi {
    export interface Options {
        authService: AuthService
        binjpipeUrl: string
    }

    export interface Response {
        id: number
        dob: string
        email: string
        avatar?: string
        username: string
        provider?: string
        mixpanelId?: string
    }

    export interface UpdateRequest {
        username?: string
        confirmPassword?: string,
        dob?: string
        avatar?: string
    }

    export interface UserEntitlement {
        user_id: number
        entitlement: Entitlement
        status: string
        date_created: Date
        date_updated: Date
    }

    export interface Entitlement {
        id: number
        slug: string
        name: string
    }
}
